import React, { useState } from 'react';
import PropTypes from 'prop-types';
import useDispatch from 'client/hooks/useDispatch';
import requestUtilityApi from 'client/actions/requestUtilityApi';

import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';

import {
  SR_API_ACTION_UTILITY_INVOICE_CREATE,
  SR_API_ACTION_UTILITY_INVOICE_UPDATE,
} from 'api/utilityActionTypes';

import { DATA_TABLE_SOURCE_NON_INTERNET_INVOICES } from 'common/dataTableConstants';

import { UTILITY_INVOICE_STATUS_PAID } from 'common/utility/constants';

import {
  accountingDashboardHeaders,
  makeAccountingDashboardRows,
} from 'client/components/UPAdminAccountingNonInternetDashboard/accountingDashboardRows';
import { SR_TABLE_THEME_PARTNER_CLEAR } from 'client/components/SrTable';
import SrButton, {
  SR_BUTTON_HEIGHT_SHORT,
  SR_BUTTON_THEME_ADOBE_GREEN,
} from 'client/components/SrButton/SrButton';
import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrFlex from 'client/components/SrFlex';
import SrLink from 'client/components/SrLink/SrLink';
import SrDataTableConnected from 'client/components/SrDataTableConnected';
import SrDataTableUI from 'client/components/SrDataTableUI';
import UPAdminAccountingNonInternetAddInvoiceModal from 'client/components/UPAdminAccountingNonInternetDashboard/UPAdminAccountingNonInternetAddInvoiceModal';

const UPAdminAccountingNonInternetDashboard = ({ utilityTypes }) => {
  const dispatch = useDispatch();
  const [highlightRowId, setHighlightRowId] = useState(null);
  const [createInvoiceModalArgs, setCreateInvoiceModalArgs] = useState(null);

  const onAddInvoice = () => {
    setCreateInvoiceModalArgs({
      utilityTypes: utilityTypes,
      onCancel: () => setCreateInvoiceModalArgs(null),
      onSave: (formValues) => {
        const activation = formValues.activation || {};
        const activationItems = activation.items || [];
        const utilityActivationItem = activationItems.find(({ utilityTypeId }) => {
          return utilityTypeId === formValues.utilityTypeId;
        });

        return dispatch(requestUtilityApi, {
          action: SR_API_ACTION_UTILITY_INVOICE_CREATE,
          payload: {
            amount: formValues.commissionAmount,
            dueDate: momentWithAustinTimezone().toISOString(),
            utilityExternalInvoiceId: formValues.externalInvoiceId,
            utilityActivationItemId: utilityActivationItem?.id,
            utilityPartnerId: activation?.utilityPartnerId,
          },
        }).then(({ invoice }) => {
          dispatch(requestUtilityApi, {
            action: SR_API_ACTION_UTILITY_INVOICE_UPDATE,
            payload: {
              id: invoice.id,
              updates: {
                status: UTILITY_INVOICE_STATUS_PAID,
              },
            },
            onSuccess: () => {
              setCreateInvoiceModalArgs(null);
              global.location.reload();
            },
          });
        });
      },
    });
  };

  return (
    <div>
      <SrDataTableConnected
        dataSource={DATA_TABLE_SOURCE_NON_INTERNET_INVOICES}
        initialViewSettings={{
          sort: {
            invoiceCreatedAt: 'desc',
          },
        }}
        count={20}
        itemsPropName="rows"
        resetOnUnmount
      >
        {({
          rows,
          viewSettings,
          dataTableIsFullyLoaded,
          dataTableApiState,
          onInfiniteScrollWrapperRef,
        }) => {
          const processedRows = makeAccountingDashboardRows({
            rows,
            highlightRowId,
            setHighlightRowId,
          });

          return (
            <div ref={onInfiniteScrollWrapperRef}>
              <SrBox padding="20px">
                <SrFlex gap="20px" align="baseline" justify="between">
                  <SrBox display="flex" direction="row">
                    <SrText font="greycliff" weight="700" fontSize="28px" lineHeight="34px">
                      Accounting Dashboard (Non-Internet)
                    </SrText>
                    <SrBox marginLeft="20px">
                      <SrLink to="/utility-profit/admin/dashboard/accounting" target="_blank">
                        <SrText
                          font="greycliff"
                          color="blue300"
                          cursor="pointer"
                          weight="800"
                          fontSize="16px"
                          lineHeight="50px"
                        >
                          Go to Internet Accounting Dashboard
                        </SrText>
                      </SrLink>
                    </SrBox>
                  </SrBox>
                  <SrButton
                    heightType={SR_BUTTON_HEIGHT_SHORT}
                    theme={SR_BUTTON_THEME_ADOBE_GREEN}
                    onClick={onAddInvoice}
                    width="200px"
                  >
                    Add invoice
                  </SrButton>
                </SrFlex>
              </SrBox>
              <SrDataTableUI
                theme={SR_TABLE_THEME_PARTNER_CLEAR}
                dataTableViewSettings={viewSettings}
                headers={accountingDashboardHeaders}
                rows={processedRows}
                sticky
              />
              {!rows.length && dataTableIsFullyLoaded && (
                <SrBox
                  backgroundColor="white"
                  display="flex"
                  justifyContent="center"
                  padding="20px"
                >
                  <SrText font="greycliff" weight="500" fontSize="16px" fontStyle="italic">
                    No invoices found
                  </SrText>
                </SrBox>
              )}
              {dataTableApiState.inProgress && <div className="loading-ellipses" />}
              {createInvoiceModalArgs && (
                <UPAdminAccountingNonInternetAddInvoiceModal {...createInvoiceModalArgs} />
              )}
            </div>
          );
        }}
      </SrDataTableConnected>
    </div>
  );
};

UPAdminAccountingNonInternetDashboard.propTypes = {
  utilityTypes: PropTypes.array.isRequired,
};

UPAdminAccountingNonInternetDashboard.displayName = 'UPAdminAccountingNonInternetDashboard';
export default UPAdminAccountingNonInternetDashboard;
